'use client'

import { useTranslations } from 'next-intl'
import { ComponentProps, useState } from 'react'

import { useSearchFilterStore } from '@/entities/map'
import {
  MobileKeywordList,
  MobilePeriodPicker,
  MobileRecentRoomList,
  MobileSearchHistoryList,
  useKeywordSearch,
  useMobileKeywordSearch,
} from '@/features/room-search'
import { usePathname, useRouter } from '@/shared/i18n'
import { Slot } from '@/shared/lib'
import { Icon, ScreenOverlay, TextField } from '@/shared/ui'
import { MobileHeader } from '@/widgets/common'

type Props = ComponentProps<typeof ScreenOverlay.Trigger>

export const KeywordSearchTrigger = ({ children, ...props }: Props) => {
  const [isOpen, setIsOpen] = useState(false)

  const open = () => {
    setIsOpen(true)
  }

  const close = () => {
    setIsOpen(false)
  }

  return (
    <ScreenOverlay.Root open={isOpen}>
      <ScreenOverlay.Trigger onClick={open} {...props}>
        {children}
      </ScreenOverlay.Trigger>
      <KeywordSearchOverlay close={close} />
    </ScreenOverlay.Root>
  )
}

type KeywordSearchOverlayProps = {
  close: () => void
}

export const KeywordSearchOverlay = ({ close }: KeywordSearchOverlayProps) => {
  const t = useTranslations()

  const router = useRouter()
  const pathname = usePathname()

  const filterActions = useSearchFilterStore(state => state.actions)

  const search: Parameters<typeof useKeywordSearch>[0]['search'] = params => {
    if (pathname === '/guest/room') {
      const { startDate, endDate, keyword } = params

      filterActions.update({ keyword, filters: { startDate, endDate } })

      return
    }

    const searchParams = new URLSearchParams()
    Object.entries(params).forEach(([key, value]) => {
      if (value) {
        searchParams.set(key, String(value))
      }
    })

    router.push(`/guest/room?${searchParams.toString()}`)
  }

  const {
    keywordInputProps,
    onRecentHistoryClick,
    onRecommendKeywordClick,
    dateRange,
    handleDateRangeChange,
    handleDateRangeReset,
  } = useMobileKeywordSearch({
    search,
    close,
  })

  return (
    <ScreenOverlay.Content className="flex flex-col">
      <MobileHeader.Root>
        <Slot name="left">
          <MobileHeader.Back onClick={close} />
        </Slot>
        <Slot name="title">{t('search_panel.title')}</Slot>
      </MobileHeader.Root>
      <ScreenOverlay.Title className="sr-only">
        {t('search_panel.title')}
      </ScreenOverlay.Title>
      <ScreenOverlay.Description className="sr-only">
        {t('search_panel.description')}
      </ScreenOverlay.Description>

      <div className="gradient-bottom-border mobile">
        <div className="flex flex-col px-4">
          <TextField
            {...keywordInputProps}
            name="keyword"
            type="search"
            placeholder={t('search_result.search.placeholder')}
            autoFocus
            className="typo-body2 h-14 border-none bg-transparent"
            padding={{ x: 0, y: 16 }}
            fullWidth
          >
            <Slot name="left">
              <Icon
                name="search"
                size={24}
                className="text-black"
                strokeWidth={1.5}
              />
            </Slot>
          </TextField>
          <MobilePeriodPicker
            onChange={handleDateRangeChange}
            className="w-full [&_input]:text-left"
          >
            <TextField
              readOnly
              tabIndex={-1}
              value={dateRange}
              placeholder={t('search_result.search.dates')}
              className="typo-body2 h-14 border-none bg-transparent *:read-only:cursor-pointer"
              padding={{ x: 0, y: 13 }}
              fullWidth
              editable
              onReset={handleDateRangeReset}
            >
              <Slot name="left">
                <Icon
                  name="calendar"
                  size={24}
                  className="text-black"
                  strokeWidth={1.5}
                />
              </Slot>
            </TextField>
          </MobilePeriodPicker>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto overscroll-contain px-4 py-10">
        <MobileKeywordList onClick={onRecommendKeywordClick} />
        <MobileSearchHistoryList
          onItemClick={onRecentHistoryClick}
          className="pt-6 not-last:pb-6"
        />
        <MobileRecentRoomList className="not-first:pt-6" />
      </div>
    </ScreenOverlay.Content>
  )
}
